import { Box, Stack } from '@mui/material';
import { Session } from '@supabase/supabase-js';
import { useMemo } from 'react';

import { environment } from '@env';

import { useDocumentExtract } from '@/api/endpoints/document/useDocumentExtract';
import { useGetDocument } from '@/api/endpoints/document/useGetDocument';
import { useLeaseExtract } from '@/api/endpoints/lease/useLeaseExtract';

import ChatWidget from '@/components/chat-widget/ChatWidget';
import DocumentExtractPanel from '@/components/document/DocumentExtractPanel';
import DocumentSummary from '@/components/document/DocumentSummary';
import DocumentUploadV2Button from '@/components/document/DocumentUploadV2Button';
import LeaseExtractPanel from '@/components/document/LeaseExtractPanel';
import MuiPDFDocument from '@/components/document/MuiPDFDocument';

// Import the interface from DocumentExtractPanel for type casting
interface ExtractedDocumentData {
  success: boolean;
  summary?: string;
  document_type?: string;
  custom_metadata?: {
    provider?: string;
    bill_type?: string;
    amount_due?: number;
    due_date?: string;
    bill_date?: string;
    account_number?: string;
    billing_period?: {
      start?: string;
      end?: string;
    };
    property_address?: string;
    user?: {
      first_name?: string;
      last_name?: string;
      email?: string | null;
      phone?: string | null;
    };
    payment_status?: string;
    autopay_enabled?: boolean;
    electric_details?: {
      total: number;
      supply: number;
      delivery: number;
      ev_credit?: number;
    };
    gas_details?: {
      total: number;
      supply: number;
      delivery: number;
    };
    total_usage?: {
      gas_therms?: number;
      electric_kwh?: number;
    };
  };
  id?: number;
  ocred?: boolean;
  checksum?: string;
  file_url?: string;
  data_type?: string;
  user_uuid?: string | null;
  created_at?: string;
  updated_at?: string;
  website_id?: number | null;
}

interface DocumentViewerProps {
  id: string;
  session: Session | null;
  layoutType?: 'page' | 'modal';
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ id, session, layoutType = 'page' }) => {
  const documentUrl = `${environment.api}/document/doc/${id}?token=${session?.access_token}`;

  const fetchDocumentQuery = useGetDocument(id, session);

  // Check if the document is a lease document by checking category name or ID
  const isLeaseDocument = useMemo(() => {
    const categoryId = fetchDocumentQuery.data?.category_id;
    const categoryName = fetchDocumentQuery.data?.category?.name;

    // Check if it's category ID 2 or has 'lease' in the category name
    if (categoryId !== undefined) {
      return categoryId?.toString() === '2';
    }

    return categoryName?.toLowerCase().includes('lease') || false;
  }, [fetchDocumentQuery.data]);

  const leaseExtractionQuery = useLeaseExtract(id, !!session?.access_token && isLeaseDocument);
  const documentExtractionQuery = useDocumentExtract(
    id,
    !!session?.access_token && !isLeaseDocument
  );

  const isImage = fetchDocumentQuery.data?.mime_type?.startsWith('image/');

  const hasExtractedData = isLeaseDocument
    ? !!leaseExtractionQuery.data
    : !!documentExtractionQuery.data;

  // Check if document needs processing
  const needsProcessing =
    fetchDocumentQuery.data?.status === 'NEW' ||
    fetchDocumentQuery.data?.status === 'FAILED' ||
    (!hasExtractedData && fetchDocumentQuery.data?.status === 'PROCESSED');

  const handleUploadComplete = () => {
    // Refetch document data after processing
    fetchDocumentQuery.refetch();
    if (isLeaseDocument) {
      leaseExtractionQuery.refetch();
    } else {
      documentExtractionQuery.refetch();
    }
  };

  if (hasExtractedData) {
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ flex: '1 1 100%', overflow: 'hidden', position: 'relative' }}
      >
        <Box sx={{ height: '100%', overflow: 'auto', flex: { xs: '1 1 100%', md: '7 0 0%' } }}>
          {isImage ? (
            <img
              src={documentUrl}
              alt={fetchDocumentQuery.data?.original_name || id}
              style={{ width: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <MuiPDFDocument
              url={documentUrl}
              fileName={fetchDocumentQuery.data?.original_name || id}
            />
          )}
        </Box>
        <Box sx={{ height: '100%', overflow: 'auto', flex: { xs: '1 1 100%', md: '5 0 0%' } }}>
          <DocumentSummary documentId={id} />
          {isLeaseDocument ? (
            <LeaseExtractPanel
              data={leaseExtractionQuery.data}
              isLoading={leaseExtractionQuery.isLoading || leaseExtractionQuery.isFetching}
              error={leaseExtractionQuery.error as Error | undefined}
            />
          ) : (
            <DocumentExtractPanel
              data={documentExtractionQuery.data?.document as ExtractedDocumentData}
              isLoading={documentExtractionQuery.isLoading || documentExtractionQuery.isFetching}
              error={documentExtractionQuery.error as Error | undefined}
            />
          )}
        </Box>
        <ChatWidget documentId={id} />
      </Stack>
    );
  }

  return (
    <Box sx={{ flex: '1 1 100px', overflow: 'auto', position: 'relative' }}>
      {isImage ? (
        <img
          src={documentUrl}
          alt={fetchDocumentQuery.data?.original_name || id}
          style={{ width: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <MuiPDFDocument url={documentUrl} fileName={fetchDocumentQuery.data?.original_name || id} />
      )}

      {needsProcessing && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            p: 2,
            boxShadow: 2,
            maxWidth: 300,
          }}
        >
          <DocumentUploadV2Button
            documentId={id}
            documentUrl={`gs://terrai-app-documents_${
              environment.stage === 'local' ? 'development' : environment.stage
            }/user-files/${session?.user?.id}/${id}.${fetchDocumentQuery.data?.original_name
              ?.split('.')
              .pop()}`}
            onUploadComplete={handleUploadComplete}
          />
        </Box>
      )}

      <ChatWidget documentId={id} />
    </Box>
  );
};

export default DocumentViewer;
